import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { LabeledMenuComponent, MenuItem } from '@mode/shared/ui';
import { BehaviorSubject, fromEvent, merge, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CommonModule, DOCUMENT } from '@angular/common';

export const AUTOCOMPLETE_FILTERS_SELECTOR = 'mode-autocomplete-filters-element';
export const CONTENT_TYPE_CHANGE_EVENT_TYPE: Readonly<string> = 'SelectionChange';
export const MATCH_ON_CHANGE_EVENT_TYPE: Readonly<string> = 'MatchOnFilterChange';
export const CREATED_BY_ME_CHANGE_EVENT_TYPE: Readonly<string> = 'CreatedByMeFilterChange';

@Component({
  standalone: true,
  selector: AUTOCOMPLETE_FILTERS_SELECTOR,
  templateUrl: './autocomplete-filters.component.html',
  styleUrls: ['./autocomplete-filters.component.scss'],
  imports: [CommonModule, LabeledMenuComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AutocompleteFiltersComponent implements OnDestroy, OnInit, OnChanges {
  private _destroy$ = new Subject<void>();
  private _menuDestroy$ = new Subject<void>();
  protected _filterInit$ = new BehaviorSubject<'idle' | 'pending' | 'initialized'>('idle');

  @ViewChild('filtersRef')
  filtersRef!: ElementRef<Element>;

  @Input()
  contentTypeItems: MenuItem[] = [];

  @Input()
  matchOnItems: MenuItem[] = [];

  @Input()
  selectedContentTypeItems: MenuItem[] = [];

  @Input()
  selectedMatchOnItems: MenuItem[] = [];

  @Input()
  dataIsOpen!: string;

  constructor(@Inject(DOCUMENT) private document: Document) {}

  ngOnInit() {
    this._filterInit$.next('pending');
  }

  ngOnChanges() {
    if (this._filterInit$.getValue() === 'pending') {
      this._filterInit$.next('initialized');
    }
  }

  ngOnDestroy() {
    this._destroy$.next();
    this._destroy$.complete();
    this._menuDestroy$.complete();
  }

  onMenuOpen() {
    // grab element ref here because the overlay container isn't available
    // until the dropdown menu opens
    const menuOverlayElem = this.document.querySelector('.cdk-overlay-container');

    // capture any click outside the dropdown this prevents the global search prompt from closing
    this.blockClicks(menuOverlayElem);
  }

  onMenuClose() {
    this._menuDestroy$.next();
  }

  onChangeContentType($item: MenuItem | MenuItem[]) {
    this.filtersRef.nativeElement.dispatchEvent(
      new CustomEvent<MenuItem | MenuItem[]>(CONTENT_TYPE_CHANGE_EVENT_TYPE, {
        detail: [$item].flat(1),
        bubbles: true,
      })
    );
  }

  onChangeMatchOn($item: MenuItem | MenuItem[]) {
    this.filtersRef.nativeElement.dispatchEvent(
      new CustomEvent<MenuItem | MenuItem[]>(MATCH_ON_CHANGE_EVENT_TYPE, {
        detail: [$item].flat(1),
        bubbles: true,
      })
    );
  }

  onChangeCreatedByMe({ target }: Event) {
    this.filtersRef.nativeElement.dispatchEvent(
      new CustomEvent<MenuItem<boolean>>(CREATED_BY_ME_CHANGE_EVENT_TYPE, {
        detail: {
          name: 'created_by_me',
          value: (target as HTMLInputElement)?.checked,
        },
        bubbles: true,
      })
    );
  }

  /**
   * Prevent touch and mouse clicks from escaping the target element
   */
  blockClicks(target: Element | null): void {
    if (target) {
      merge(fromEvent(target, 'mousedown'), fromEvent(target, 'touchstart'))
        .pipe(takeUntil(merge(this._destroy$, this._menuDestroy$)))
        .subscribe((event) => event.stopPropagation());
    }
  }
}
